import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Phone, Smartphone, HardDrive, DollarSign, ChevronDown, ChevronUp, 
  RefreshCw, Clock, Info, CheckCircle2, AlertCircle, Download, FileSpreadsheet,
  Copy, Check
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PlantaVivoSnapshot, LinhaDetalhe } from '@/hooks/usePlantaVivoSnapshot';
import { ClickToCall } from '@/components/common/ClickToCall';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PlantaVivoDisplayProps {
  snapshot: PlantaVivoSnapshot;
  leadId?: string;
  onReimport?: () => void;
  isReimporting?: boolean;
  modoAmplo?: boolean;
}

// Formata telefone para exibição
function formatarTelefone(telefone: string): string {
  const digits = telefone.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return telefone;
}

// Formata valor monetário
function formatarValor(valor: number): string {
  const n = typeof valor === 'number' && isFinite(valor) ? valor : 0;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Verifica se está apto para portabilidade (M >= 24)
function isApto(m: number | null): boolean {
  return m !== null && m >= 24;
}

// Formata GB de forma segura (tolera null/undefined/NaN)
function fmtGb(v: any, dec = 0): string {
  const n = typeof v === 'number' && isFinite(v)
    ? v
    : (typeof v === 'string' && isFinite(Number(v)) ? Number(v) : 0);
  return n.toFixed(dec);
}

export function PlantaVivoDisplay({ snapshot, leadId, onReimport, isReimporting, modoAmplo = false }: PlantaVivoDisplayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  const linhas = snapshot.linhas_detalhadas || [];
  const linhasAptas = linhas.filter(l => isApto(l.m));

  // Extrair url do arquivo dos dados_brutos
  const arquivoUrl = (snapshot.dados_brutos as any)?.arquivo_url as string | null | undefined;
  const nomeArquivo = (snapshot.dados_brutos as any)?.nome_arquivo as string | null | undefined;

  const handleDownload = async () => {
    if (!arquivoUrl) return;
    setIsDownloading(true);
    try {
      const { data, error } = await supabase.storage
        .from('faturas')
        .createSignedUrl(arquivoUrl, 120);

      if (error || !data?.signedUrl) {
        throw new Error(error?.message || 'Erro ao gerar link de download');
      }

      // Abre o link em nova aba para download
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = nomeArquivo || 'planta_vivo.xlsx';
      a.target = '_blank';
      a.click();
    } catch (err: any) {
      toast.error(`Erro ao baixar arquivo: ${err.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopiarTodasLinhas = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const texto = linhas.map(l => l.telefone.replace(/\D/g, '')).join('\n');
    try {
      await navigator.clipboard.writeText(texto);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
      toast.success(`${linhas.length} número${linhas.length !== 1 ? 's' : ''} copiado${linhas.length !== 1 ? 's' : ''}!`);
    } catch {
      toast.error('Não copiou. Selecione o texto e copie na mão.');
    }
  };

  return (
    <div className="space-y-4">
      {/* Cards de resumo */}
      <div className="grid grid-cols-3 gap-3">
        {/* Total de Linhas */}
        <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
          <div className="flex items-center gap-2 mb-1">
            <Smartphone className="h-4 w-4 text-primary" />
            <span className="text-xs text-muted-foreground">Linhas Móveis</span>
          </div>
          <p className="text-2xl font-bold text-primary">{snapshot.total_linhas}</p>
          {linhasAptas.length > 0 && (
            <p className="text-xs text-success mt-0.5">
              {linhasAptas.length} aptas
            </p>
          )}
        </div>

        {/* Valor Total */}
        <div className="p-3 rounded-lg bg-success/10 border border-success/20">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-success" />
            <span className="text-xs text-muted-foreground">Valor Total</span>
          </div>
          <p className="text-2xl font-bold text-success">
            {formatarValor(snapshot.valor_total)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">/mês</p>
        </div>

        {/* GB Total */}
        <div className="p-3 rounded-lg bg-info/10 border border-info/20">
          <div className="flex items-center gap-2 mb-1">
            <HardDrive className="h-4 w-4 text-info" />
            <span className="text-xs text-muted-foreground">GB Total</span>
          </div>
          <p className="text-2xl font-bold text-info">
            {fmtGb(snapshot.gb_total)} GB
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">contratado</p>
        </div>
      </div>

      {/* Arquivo disponível para download */}
      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FileSpreadsheet className="h-4 w-4 shrink-0" />
          <span className="truncate max-w-[200px]" title={nomeArquivo || undefined}>
            {nomeArquivo || (snapshot.dados_brutos as any)?.arquivo || 'Planilha importada'}
          </span>
        </div>
        {arquivoUrl ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading}
            className="gap-1.5 text-xs h-7 shrink-0"
          >
            {isDownloading ? (
              <><span className="animate-spin">⏳</span> Baixando...</>
            ) : (
              <><Download className="h-3.5 w-3.5" /> Baixar Planta</>
            )}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground italic">Arquivo não disponível</span>
        )}
      </div>

      {/* Tabela expansível de linhas */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-2 h-auto">
            <span className="text-sm font-medium flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Ver detalhes das {linhas.length} linhas
            </span>
            <div className="flex items-center gap-1">
              {linhas.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopiarTodasLinhas}
                  className="h-7 px-2 text-xs gap-1"
                >
                  {copiedAll ? (
                    <><Check className="h-3.5 w-3.5 text-success" /> Copiado!</>
                  ) : (
                    <><Copy className="h-3.5 w-3.5" /> Copiar Linhas</>
                  )}
                </Button>
              )}
              {isOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-2">
          <div className="border rounded-lg overflow-hidden">
            <div className={modoAmplo ? 'max-h-[50vh] overflow-auto' : 'max-h-[400px] overflow-auto'}>
              <Table>
                <TableHeader className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                  <TableRow>
                    <TableHead className="px-2 py-1.5 w-[130px] text-xs">Telefone</TableHead>
                    <TableHead className="px-2 py-1.5 text-xs">Plano</TableHead>
                    <TableHead className="px-2 py-1.5 w-[50px] text-center text-xs">GB</TableHead>
                    <TableHead className="px-2 py-1.5 w-[80px] text-right text-xs">Valor</TableHead>
                    <TableHead className="px-2 py-1.5 w-[45px] text-center text-xs">M</TableHead>
                    <TableHead className="px-2 py-1.5 w-[70px] text-center text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linhas.map((linha, idx) => (
                    <TableRow key={idx} className="hover:bg-muted/50">
                      <TableCell className="font-mono text-xs p-1.5">
                        <ClickToCall telefone={linha.telefone} leadId={leadId}>
                          <span className="text-primary hover:underline cursor-pointer flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {formatarTelefone(linha.telefone)}
                          </span>
                        </ClickToCall>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm truncate max-w-[200px]" title={linha.plano}>
                            {linha.plano || '-'}
                          </span>
                          {linha.servicos && linha.servicos.length > 0 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="text-xs px-1.5">
                                    +{linha.servicos.length}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-[250px]">
                                  <p className="font-medium mb-1">Serviços:</p>
                                  <ul className="text-xs space-y-0.5">
                                    {linha.servicos.slice(0, 10).map((s, i) => (
                                      <li key={i}>• {s}</li>
                                    ))}
                                    {linha.servicos.length > 10 && (
                                      <li className="text-muted-foreground">
                                        +{linha.servicos.length - 10} mais...
                                      </li>
                                    )}
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center p-1.5">
                        <span className="text-xs font-medium">{linha.gb || 0}</span>
                      </TableCell>
                      <TableCell className="text-right font-medium text-xs p-1.5">
                        {formatarValor(linha.valor)}
                      </TableCell>
                      <TableCell className="text-center">
                        {linha.m !== null ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge 
                                  variant="outline"
                                  className={isApto(linha.m) 
                                    ? 'bg-success/10 text-success border-success' 
                                    : 'bg-warning/10 text-warning border-warning'
                                  }
                                >
                                  {linha.m}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="flex items-center gap-1.5">
                                  {isApto(linha.m) ? (
                                    <>
                                      <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                                      <span>Sem multa (≥24 meses)</span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertCircle className="h-3.5 w-3.5 text-warning" />
                                      <span>Com multa (&lt;24 meses)</span>
                                    </>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline"
                          className={linha.estado?.toLowerCase() === 'ativo'
                            ? 'bg-success/10 text-success border-success'
                            : 'bg-muted text-muted-foreground'
                          }
                        >
                          {linha.estado || 'Ativo'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Footer com data e reimport */}
      <div className="flex items-center justify-between pt-2 border-t">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>
            Importado em {format(new Date(snapshot.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
          </span>
        </div>

        {onReimport && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReimport}
            disabled={isReimporting}
            className="gap-1.5 text-xs h-7"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isReimporting ? 'animate-spin' : ''}`} />
            Reimportar
          </Button>
        )}
      </div>
    </div>
  );
}
